import type { HotelPolicyLimits } from './hotel_offer_projection.js';
import {
  HOTEL_REFINE_AMENITIES_CAP,
  type ChatUiHotelOffer,
  type HotelRefineOffer,
} from './hotel_planner_schema.js';

/**
 * Projeta a oferta do CARD para a forma que viaja no contexto de refino (#2628):
 * só os campos que a cena do agente lê (`HotelRefineOfferSchema`), amenities
 * reduzidas a `{ label }` e cortadas no mesmo teto da cena, sem chave
 * `undefined` (o wire e o `toEqual` dos testes ficam limpos). Puro, sem IO.
 */
export function toHotelRefineOffer(offer: ChatUiHotelOffer): HotelRefineOffer {
  const amenities = offer.amenities
    ?.slice(0, HOTEL_REFINE_AMENITIES_CAP)
    .map((amenity) => ({ label: amenity.label }));
  const picked: HotelRefineOffer = {
    offerId: offer.offerId,
    title: offer.title,
    city: offer.city,
    country: offer.country,
    category: offer.category,
    rating: offer.rating,
    reviewCount: offer.reviewCount,
    price: offer.price,
    refundable: offer.refundable,
    breakfast: offer.breakfast,
    policyStatus: offer.policyStatus,
    policyViolations: offer.policyViolations?.length ? offer.policyViolations : undefined,
    latitude: offer.latitude,
    longitude: offer.longitude,
    distanceKm: offer.distanceKm,
    amenities: amenities && amenities.length > 0 ? amenities : undefined,
  };
  return Object.fromEntries(
    Object.entries(picked as Record<string, unknown>).filter(([, value]) => value !== undefined),
  ) as HotelRefineOffer;
}

/**
 * Os limites da política que valem para a busca INTEIRA, tirados da primeira
 * oferta que os traz. Eles são do VIAJANTE — idênticos em toda oferta — então
 * viajam UMA vez no contexto de refino (`HotelPlannerRefineContext.policyLimits`)
 * em vez de por oferta, onde N cópias pagariam o orçamento do WAF N vezes (é
 * por isso que `HotelRefineOfferSchema` deixa o campo de fora). `undefined`
 * quando nenhuma oferta tem limites — busca sem política. Puro, sem IO.
 */
export function policyLimitsFromOffers(
  offers: readonly ChatUiHotelOffer[],
): HotelPolicyLimits | undefined {
  return offers.find((offer) => offer.policyLimits && Object.keys(offer.policyLimits).length > 0)
    ?.policyLimits;
}
